
import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowRight, Clock, AlertTriangle } from 'lucide-react';

type PopupType = 'exit' | 'scroll' | null;

const PopupController: React.FC = () => {
  const [activePopup, setActivePopup] = useState<PopupType>(null);
  const [isVisible, setIsVisible] = useState(false);

  const STORAGE_KEY = 'gbenlandia_last_popup_time';
  const FREQUENCY_DAYS = 1;

  const closePopup = () => {
    setIsVisible(false);
    setTimeout(() => setActivePopup(null), 300); // Wait for transition
  };

  const showPopup = useCallback((type: PopupType) => {
    const lastShown = localStorage.getItem(STORAGE_KEY);
    const now = Date.now();
    if (lastShown && now - parseInt(lastShown) < FREQUENCY_DAYS * 24 * 60 * 60 * 1000) {
      return;
    }

    if (sessionStorage.getItem('gbenlandia_popup_shown_session')) {
      return;
    }

    setActivePopup(type);
    setIsVisible(true);
    
    localStorage.setItem(STORAGE_KEY, now.toString());
    sessionStorage.setItem('gbenlandia_popup_shown_session', 'true');
  }, []);

  useEffect(() => {
    if (activePopup) return;

    const timer = setTimeout(() => {
      showPopup('scroll');
    }, 30000);

    const handleScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight;
      const totalHeight = document.documentElement.scrollHeight;
      const scrollPercent = (scrollPos / totalHeight) * 100;

      if (scrollPercent >= 40 && !activePopup) {
        showPopup('scroll');
      }

      if (window.innerWidth < 768 && scrollPercent >= 60 && !activePopup) {
        showPopup('exit');
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      if (window.innerWidth >= 768 && e.clientY <= 10) {
        showPopup('exit');
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mouseleave', handleMouseOut);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseOut);
    };
  }, [activePopup, showPopup]);

  if (!activePopup) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-6 transition-all duration-500 ${isVisible ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm"
        onClick={closePopup}
      ></div>

      {/* Modal Container */}
      <div 
        className={`relative w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'}`}
      >
        <button 
          onClick={closePopup}
          className="absolute top-4 right-4 text-slate-500 hover:text-white p-2"
        >
          <X size={20} />
        </button>

        <div className="p-8 md:p-12">
          {activePopup === 'exit' ? (
            <div className="space-y-6">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-500 mb-2">
                <AlertTriangle size={24} />
              </div>
              <h3 className="text-3xl font-bold text-white tracking-tight">You have two options:</h3>
              <div className="space-y-4">
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                  <span className="text-xs font-mono text-slate-500 uppercase block mb-1">Option 1</span>
                  <p className="text-slate-300">Keep researching "best GTM automation tools" and doing it yourself.</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                  <span className="text-xs font-mono text-emerald-400 uppercase block mb-1">Option 2</span>
                  <p className="text-white font-medium">Get on a 20-minute call with someone who's already built what you're Googling.</p>
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-4">
                <button 
                  data-cal-namespace="gtm-audit-revenue-bottleneck-identification"
                  data-cal-link="gbenlandia/gtm-audit-revenue-bottleneck-identification"
                  data-cal-config='{"layout":"month_view","theme":"dark"}'
                  onClick={closePopup}
                  className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-bold transition-all text-center flex items-center justify-center gap-2"
                >
                  Option 2 Sounds Smarter <ArrowRight size={18} />
                </button>
                <button 
                  onClick={closePopup}
                  className="text-slate-500 hover:text-slate-300 text-sm font-medium transition-colors"
                >
                  I'll take my chances
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 mb-2">
                <Clock size={24} />
              </div>
              <h3 className="text-3xl font-bold text-white tracking-tight leading-tight">You've been here 30 seconds.</h3>
              <p className="text-lg text-slate-400 leading-relaxed">
                That's long enough to know I'm not like the others. I don't do discovery calls that waste your time. 
                <span className="text-white"> I do technical audits that expose what's actually costing you revenue. </span> 
                20 minutes. Calendar's open.
              </p>
              <div className="flex flex-col gap-3 pt-4">
                <button 
                  data-cal-namespace="gtm-audit-revenue-bottleneck-identification"
                  data-cal-link="gbenlandia/gtm-audit-revenue-bottleneck-identification"
                  data-cal-config='{"layout":"month_view","theme":"dark"}'
                  onClick={closePopup}
                  className="w-full py-4 bg-white text-slate-900 hover:bg-emerald-400 transition-all rounded-lg font-bold text-center flex items-center justify-center gap-2"
                >
                  Audit My Stack <ArrowRight size={18} />
                </button>
                <button 
                  onClick={closePopup}
                  className="text-slate-500 hover:text-slate-300 text-sm font-medium transition-colors"
                >
                  Keep browsing
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopupController;
